import { Injectable, inject } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import Keycloak from 'keycloak-js';
import { ClearConfigurationCacheAction } from 'src/app/dsmb/store/configurations/configuration.action';
import {
  ClearMeetingSessionDataAction,
  ClearMeetingTypesConfigurationCacheAction,
} from 'src/app/dsmb/store/meeting/meeting.actions';
import { StringEmpty } from '../../utils/global-vars';
import { ClearOperationCacheAction } from '../operation/operation.actions';
import { ClearOrganizationCacheAction } from '../organization/organization.actions';
import { ToggleSidebarItemAction } from '../sidebar/sidebar.actions';
import { ClearStudyCacheAction } from '../study/study.actions';
import { ClearUserStateAction } from '../user/user.actions';
import { LogoutAction } from './auth.actions';
import { AUTH_STATE_TOKEN } from './auth.token';

/**
 * Tokens and the authenticated session are owned by Keycloak, so this state no
 * longer holds credentials. It only orchestrates logout: clearing the in-app
 * caches before handing off to the Keycloak end-session endpoint.
 */
export type IAuthState = Record<string, never>;

@State<IAuthState>({
  name: AUTH_STATE_TOKEN,
  defaults: AuthState.defaultState,
})
@Injectable()
export class AuthState {
  static defaultState: IAuthState = {};

  private readonly keycloak = inject(Keycloak);

  @Action(LogoutAction)
  authActionsLogout({ dispatch }: StateContext<IAuthState>, { redirectPath }: LogoutAction) {
    dispatch([
      new ToggleSidebarItemAction(StringEmpty),
      new ClearOperationCacheAction(),
      new ClearOrganizationCacheAction(),
      new ClearStudyCacheAction(),
      new ClearConfigurationCacheAction(),
      new ClearMeetingTypesConfigurationCacheAction(),
      new ClearMeetingSessionDataAction(),
      new ClearUserStateAction(),
    ]);

    const redirectUri = `${window.location.origin}/#${redirectPath || '/login'}`;

    // When the Keycloak session is already gone client-side (tokens cleared
    // after a failed refresh) there is nothing to end on the server, and the
    // end-session endpoint would show a logout-confirmation page because no
    // id_token_hint can be sent. Land directly on the target page instead.
    if (!this.keycloak.authenticated || !this.keycloak.idToken) {
      window.location.assign(redirectUri);
      return undefined;
    }

    return this.keycloak.logout({ redirectUri });
  }
}
